// ④/⑤で使うブループリント（ゲーム同梱の完成建造物カタログ）の読み込みと fill 展開。
// 公開ツールの自己完結原則（CLAUDE.md 規約8）に従い、sevenfold 本体ではなく
// 「スキャフォールド済みゲーム自身の maps/_shared/blueprints/」だけを参照する。
// ブループリントの配置式はエンジン（blueprint-placer）と同じ anchor + originOffset + block.pos。
import path from 'node:path';
import { readdirSync } from 'node:fs';
import { readJsonIfExists } from './json-utils.mjs';
import { pipelineConfig, str, asArray } from './story-shared.mjs';

const catalogCache = new Map();

function blueprintsDir(gameDir) {
    return path.join(gameDir, 'maps/_shared/blueprints');
}

/**
 * ゲームに同梱されたブループリントのカタログ（軽量メタ情報のみ）。
 * ボックス数が landmark.maxBlueprintBoxes を超える巨大定義はマップ生成には重いため除外する
 * （巨大構造は landmarks の custom（自由 fill 造形）で表現する方針）。
 * @returns {Array<{ id, label, size, originOffset, boxCount }>}
 */
export function loadBlueprintCatalog(gameDir) {
    if (!gameDir) return [];
    if (catalogCache.has(gameDir)) return catalogCache.get(gameDir);
    const maxBoxes = pipelineConfig().worldDesign.engine.landmark.maxBlueprintBoxes;
    const catalog = [];
    let files = [];
    try {
        files = readdirSync(blueprintsDir(gameDir)).filter((file) => file.endsWith('.json'));
    } catch {
        catalogCache.set(gameDir, catalog);
        return catalog;
    }
    for (const file of files.sort()) {
        const doc = readJsonIfExists(path.join(blueprintsDir(gameDir), file));
        const bp = doc?.blueprint;
        if (!bp || !str(bp.blueprintId) || !Array.isArray(bp.blocks)) continue;
        if (bp.blocks.length > maxBoxes) continue;
        catalog.push({
            id: bp.blueprintId,
            label: str(bp.displayName) || bp.blueprintId,
            size: asArray(bp.size).length === 3 ? [...bp.size] : null,
            originOffset: asArray(bp.originOffset).length === 3 ? [...bp.originOffset] : [0, 0, 0],
            boxCount: bp.blocks.length,
        });
    }
    catalogCache.set(gameDir, catalog);
    return catalog;
}

/** ④プロンプトへ渡すカタログ一覧（id / 名称 / サイズ）。空なら利用不可の説明を返す。 */
export function renderBlueprintCatalog(catalog) {
    if (!catalog.length) return '（このプロジェクトではブループリントを利用できません。kind=blueprint は使わず、custom の fills で造形してください）';
    return catalog
        .map((entry) => `- ${entry.id}（${entry.label}） サイズ ${entry.size ? entry.size.join('×') : '不明'}`)
        .join('\n');
}

/** 選択されたブループリント本体（blocks 含む）を読み込む。 */
export function loadBlueprintDefinition(gameDir, blueprintId) {
    if (!gameDir || !/^[A-Za-z0-9_]+$/.test(String(blueprintId ?? ''))) return null;
    const doc = readJsonIfExists(path.join(blueprintsDir(gameDir), `${blueprintId}.json`));
    return doc?.blueprint ?? null;
}

/** 軸平行ボックスを 90 度単位で回転する（角2点を回して正規化。X/Z のみ・Y は不変）。 */
export function rotateBoxXZ(x, z, sizeX, sizeZ, rotation) {
    const rotatePoint = (px, pz) => {
        switch (((rotation % 360) + 360) % 360) {
            case 90: return [-pz, px];
            case 180: return [-px, -pz];
            case 270: return [pz, -px];
            default: return [px, pz];
        }
    };
    const [ax, az] = rotatePoint(x, z);
    const [bx, bz] = rotatePoint(x + sizeX - 1, z + sizeZ - 1);
    return {
        x: Math.min(ax, bx),
        z: Math.min(az, bz),
        sizeX: Math.abs(ax - bx) + 1,
        sizeZ: Math.abs(az - bz) + 1,
    };
}

/** ブループリントの設置フットプリント（XZ 矩形・回転込み・アンカー絶対座標）。 */
export function blueprintFootprintRect(entry, position, rotation) {
    const size = entry.size ?? [1, 1, 1];
    const origin = entry.originOffset ?? [0, 0, 0];
    const local = rotateBoxXZ(origin[0], origin[2], Math.max(1, size[0]), Math.max(1, size[2]), rotation);
    return {
        minX: position[0] + local.x,
        maxX: position[0] + local.x + local.sizeX,
        minZ: position[1] + local.z,
        maxZ: position[1] + local.z + local.sizeZ,
    };
}

/**
 * ブループリントを blockFill 群へ展開する（マップロード時に決定的に建つ）。
 * @param {object} opts { landmark, blueprint, baseY, worldHeight }
 *   landmark: { id, position: [x, z], rotation, materialOverrides }
 * @returns {Array} blockFill 配列（絶対座標）
 */
export function expandBlueprintFills({ landmark, blueprint, baseY, worldHeight = 100 }) {
    const origin = asArray(blueprint.originOffset).length === 3 ? blueprint.originOffset : [0, 0, 0];
    const overrides = landmark.materialOverrides ?? {};
    const fills = [];
    for (const [index, box] of asArray(blueprint.blocks).entries()) {
        const pos = asArray(box.pos);
        const size = asArray(box.size);
        if (pos.length < 3 || size.length < 3) continue;
        const local = rotateBoxXZ(origin[0] + pos[0], origin[2] + pos[2], Math.max(1, size[0]), Math.max(1, size[2]), landmark.rotation);
        const y = baseY + origin[1] + pos[1];
        if (y >= worldHeight) continue;
        const sizeY = Math.min(Math.max(1, size[1]), worldHeight - Math.max(0, y));
        fills.push({
            id: `${landmark.id}_bp${index + 1}`,
            shape: 'rect',
            blockType: str(overrides[box.block]) || box.block,
            pos: [landmark.position[0] + local.x, Math.max(0, y), landmark.position[1] + local.z],
            size: [local.sizeX, sizeY, local.sizeZ],
        });
    }
    return fills;
}
